/**
 * 出荷予定表示
 *
 * 【通常営業日】
 * 11:59までのご注文は、08/18発送予定です。
 *
 * 【大型連休明け】
 * 11:59までのご注文は、08/17より順次発送予定です。
 *
 * - 通常の締切：12:00未満
 * - 表示上の締切：11:59
 * - 祝日：外部APIから自動取得
 * - API失敗時：手動リストにフォールバック
 * - キャッシュ：localStorage
 * - 表示：.js-shipDate01（締切） / .js-shipDate02（出荷日）
 */

(async () => {
  const CONFIG = {
    // ========================================
    // 出荷締切
    // ========================================

    // 12:00未満の注文なら当日出荷
    cutoffHour: 12,

    // 画面上に表示する締切時刻
    displayCutoff: {
      hour: 11,
      minute: 59,
    },

    // ========================================
    // 定休日
    // ========================================

    // 休業曜日
    // 0:日曜日 / 6:土曜日
    closedWeekdays: [0, 6],

    // ========================================
    // 自社休業日
    // ========================================

    // 祝日以外の休業日はここに追加
    extraClosedDates: [
      "2026-08-11",
      "2026-08-12",
      "2026-08-13",
      "2026-08-14",
    ],

    // ========================================
    // 大型連休明け
    // ========================================

    // この日付の場合だけ、
    // 「〇〇/〇〇より順次発送予定です。」
    // と表示する
    sequentialShipDates: [
      "2026-08-17",
    ],

    // ========================================
    // 祝日API障害時の予備
    // ========================================

    fallbackClosedDates: [
      // 例
      // "2026-01-01",
    ],

    // ========================================
    // 日本の祝日API
    // ========================================

    holidayApi: (year) =>
      `https://date.nager.at/api/v3/PublicHolidays/${year}/JP`,

    // ========================================
    // HTML反映先
    // ========================================

    selectors: {
      deadline: ".js-shipDate01",
      shipDate: ".js-shipDate02",
    },
  };

  // ========================================
  // 現在日時
  // ========================================

  const now = new Date();

  // 今年＋来年の祝日を取得
  // 年末年始をまたいでも対応できるようにする
  const years = [
    now.getFullYear(),
    now.getFullYear() + 1,
  ];

  const holidaySet = await buildHolidaySet(
    years,
    CONFIG
  );

  // ========================================
  // 出荷予定日を計算
  // ========================================

  const shipDate = calcShipDate(
    now,
    CONFIG,
    holidaySet
  );

  if (!shipDate) return;

  // ========================================
  // 締切日時を作成
  // ========================================

  const deadline = new Date(shipDate);

  deadline.setHours(
    CONFIG.displayCutoff.hour,
    CONFIG.displayCutoff.minute,
    0,
    0
  );

  // ========================================
  // 締切日時を表示
  // ========================================

  document
    .querySelectorAll(CONFIG.selectors.deadline)
    .forEach((el) => {
      el.textContent =
        `${fmtMMDD(deadline)} ${fmtHHmm(deadline)}`;
    });

  // ========================================
  // 出荷予定日を表示
  // ========================================

  document
    .querySelectorAll(CONFIG.selectors.shipDate)
    .forEach((el) => {

      const shipDateYMD = toYMD(shipDate);

      // 大型連休明け対象日の場合
      if (
        CONFIG.sequentialShipDates.includes(
          shipDateYMD
        )
      ) {
        el.textContent =
          `${fmtMMDD(shipDate)}より順次`;

      } else {

        // 通常営業日
        el.textContent =
          fmtMMDD(shipDate);
      }
    });

  // ========================================
  // 出荷日計算
  // ========================================

  function calcShipDate(
    nowDate,
    cfg,
    holidays
  ) {

    // 今日が営業日かつ12:00未満なら
    // 当日出荷
    if (
      !isClosed(
        nowDate,
        cfg,
        holidays
      ) &&
      nowDate.getHours() < cfg.cutoffHour
    ) {
      return startOfDay(nowDate);
    }

    // 今日が休業日、
    // または12:00以降の場合は
    // 次の営業日を探す
    let d = startOfDay(nowDate);

    for (
      let i = 0;
      i < 366;
      i++
    ) {
      d = addDays(d, 1);

      if (
        !isClosed(
          d,
          cfg,
          holidays
        )
      ) {
        return d;
      }
    }

    return null;
  }

  // ========================================
  // 休業日判定
  // ========================================

  function isClosed(
    date,
    cfg,
    holidays
  ) {

    const weekday =
      date.getDay();

    // 土日
    if (
      cfg.closedWeekdays.includes(
        weekday
      )
    ) {
      return true;
    }

    const ymd =
      toYMD(date);

    // 祝日
    if (
      holidays.has(ymd)
    ) {
      return true;
    }

    // 自社休業日
    if (
      cfg.extraClosedDates.includes(
        ymd
      )
    ) {
      return true;
    }

    return false;
  }

  // ========================================
  // 祝日一覧を作成
  // ========================================

  async function buildHolidaySet(
    years,
    cfg
  ) {

    const set =
      new Set();

    // API障害時用の予備日付を先に追加
    (
      cfg.fallbackClosedDates || []
    ).forEach((d) =>
      set.add(d)
    );

    // APIから祝日を取得
    const lists =
      await Promise.all(
        years.map((y) =>
          fetchHolidaysWithCache(
            y,
            cfg
          ).catch(() => [])
        )
      );

    lists
      .flat()
      .forEach((d) =>
        set.add(d)
      );

    return set;
  }

  // ========================================
  // 祝日API取得
  // ========================================

  async function fetchHolidaysWithCache(
    year,
    cfg
  ) {

    const cacheKey =
      `jp-holidays-${year}`;

    // 7日以内のキャッシュがあれば使用
    const cached =
      readCache(
        cacheKey,
        7
      );

    if (cached) {
      return cached;
    }

    const res =
      await fetch(
        cfg.holidayApi(year)
      );

    if (!res.ok) {
      throw new Error(
        `holiday api error: ${res.status}`
      );
    }

    const data =
      await res.json();

    // Nager.Date
    // { date: "YYYY-MM-DD", ... }
    // のdateだけ取得
    const dates =
      (
        Array.isArray(data)
          ? data
          : []
      )
        .map(
          (x) =>
            x && x.date
        )
        .filter(Boolean);

    // キャッシュ保存
    writeCache(
      cacheKey,
      dates
    );

    return dates;
  }

  // ========================================
  // キャッシュ読み込み
  // ========================================

  function readCache(
    key,
    daysValid
  ) {

    try {

      const raw =
        localStorage.getItem(
          key
        );

      if (!raw) {
        return null;
      }

      const obj =
        JSON.parse(raw);

      if (
        !obj ||
        !obj.savedAt ||
        !Array.isArray(
          obj.data
        )
      ) {
        return null;
      }

      const ageMs =
        Date.now() -
        obj.savedAt;

      const limitMs =
        daysValid *
        24 *
        60 *
        60 *
        1000;

      // 有効期限切れ
      if (
        ageMs > limitMs
      ) {
        return null;
      }

      return obj.data;

    } catch {

      return null;
    }
  }

  // ========================================
  // キャッシュ保存
  // ========================================

  function writeCache(
    key,
    data
  ) {

    try {

      localStorage.setItem(
        key,
        JSON.stringify({
          savedAt:
            Date.now(),
          data,
        })
      );

    } catch {

      // localStorageが使用できなくても
      // 出荷日表示自体は継続
    }
  }

  // ========================================
  // 日付を0:00にする
  // ========================================

  function startOfDay(date) {

    const d =
      new Date(date);

    d.setHours(
      0,
      0,
      0,
      0
    );

    return d;
  }

  // ========================================
  // 日付加算
  // ========================================

  function addDays(
    date,
    days
  ) {

    const d =
      new Date(date);

    d.setDate(
      d.getDate() +
      days
    );

    return d;
  }

  // ========================================
  // YYYY-MM-DD形式
  // ========================================

  function toYMD(date) {

    const y =
      date.getFullYear();

    const m =
      String(
        date.getMonth() + 1
      ).padStart(
        2,
        "0"
      );

    const d =
      String(
        date.getDate()
      ).padStart(
        2,
        "0"
      );

    return `${y}-${m}-${d}`;
  }

  // ========================================
  // MM/DD形式
  // ========================================

  function fmtMMDD(date) {

    const m =
      String(
        date.getMonth() + 1
      ).padStart(
        2,
        "0"
      );

    const d =
      String(
        date.getDate()
      ).padStart(
        2,
        "0"
      );

    return `${m}/${d}`;
  }

  // ========================================
  // HH:mm形式
  // ========================================

  function fmtHHmm(date) {

    const h =
      String(
        date.getHours()
      ).padStart(
        2,
        "0"
      );

    const m =
      String(
        date.getMinutes()
      ).padStart(
        2,
        "0"
      );

    return `${h}:${m}`;
  }
})();